import { Link } from 'react-router-dom'
import {
  Award,
  Banknote,
  CalendarDays,
  Clock,
  FileText,
  Gavel,
  PiggyBank,
  UserMinus,
  Users,
} from 'lucide-react'
import type { CompanyDashboardActivity } from '@/types/company.types'
import { Panel } from '@/components/ui'
import { PermissionGate } from '@/components/common/PermissionGate'
import { formatRelative } from '@/lib/formatters'

export interface CompanyRailProps {
  companyId: string
  recentActivity: CompanyDashboardActivity[]
  isLoading: boolean
}

interface RailLink {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
}

function RailLinkItem({ to, label, icon: Icon }: RailLink): React.ReactElement {
  return (
    <li>
      <Link
        to={to}
        className="group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-300 dark:hover:bg-gray-700/40 dark:hover:text-gray-50"
      >
        <Icon
          className="h-4 w-4 shrink-0 text-gray-400 transition-colors duration-150 group-hover:text-primary dark:text-gray-500 dark:group-hover:text-sky-400"
          aria-hidden
        />
        <span className="truncate">{label}</span>
      </Link>
    </li>
  )
}

/**
 * Reference material: where to go next, and what already happened.
 *
 * Everything here is deliberately low-contrast — it must never pull the eye
 * away from the attention list in the focus column.
 */
export function CompanyRail({
  companyId,
  recentActivity,
  isLoading,
}: CompanyRailProps): React.ReactElement {
  const q = `?companyId=${companyId}`
  const links: RailLink[] = [
    { to: `/members${q}`, label: 'Members', icon: Users },
    { to: `/attendance${q}`, label: 'Attendance', icon: CalendarDays },
    { to: `/time-corrections${q}`, label: 'Time corrections', icon: Clock },
    { to: `/leaves${q}`, label: 'Leave', icon: UserMinus },
    { to: `/penalties${q}`, label: 'Penalties', icon: Gavel },
    { to: `/bonuses${q}`, label: 'Bonuses', icon: Award },
    { to: `/security-ledger${q}`, label: 'Security deposit', icon: PiggyBank },
    { to: `/documents${q}`, label: 'Documents', icon: FileText },
  ]

  return (
    <div className="space-y-4">
      <Panel
        as="nav"
        aria-labelledby="rail-links-heading"
        title={
          <h3
            id="rail-links-heading"
            className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-500 dark:text-gray-400"
          >
            Go to
          </h3>
        }
        dense
        contentClassName="p-2"
      >
        <ul className="space-y-0.5">
          {links.map((link) => (
            <RailLinkItem key={link.to} {...link} />
          ))}
          <PermissionGate module="payroll" action="read_company">
            <RailLinkItem
              to={`/companies/${companyId}/payroll/runs`}
              label="Payroll runs"
              icon={Banknote}
            />
          </PermissionGate>
        </ul>
      </Panel>

      <Panel
        as="section"
        aria-labelledby="rail-activity-heading"
        title={
          <h3
            id="rail-activity-heading"
            className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-500 dark:text-gray-400"
          >
            Recent activity
          </h3>
        }
        dense
        contentClassName="px-5 py-4"
      >
        {isLoading ? (
          <div className="space-y-3" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-3.5 rounded bg-gray-100 dark:bg-gray-700" />
            ))}
            <span className="sr-only">Loading recent activity</span>
          </div>
        ) : recentActivity.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No activity recorded for this company yet.
          </p>
        ) : (
          <ol className="space-y-3">
            {recentActivity.slice(0, 6).map((item) => (
              <li key={item.id} className="flex gap-2.5">
                <span
                  className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-gray-600"
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm leading-snug text-gray-700 dark:text-gray-300">
                    {item.text}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-400 dark:text-gray-500">
                    {formatRelative(item.timestamp)}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}

        <Link
          to={`/audit-logs?companyId=${companyId}`}
          className="mt-4 inline-block border-t border-transparent pt-3 text-xs font-medium text-primary underline-offset-4 hover:underline dark:text-sky-400"
        >
          View audit log
        </Link>
      </Panel>
    </div>
  )
}

CompanyRail.displayName = 'CompanyRail'
